import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { DashboardLayout } from "@/layouts/dashboard-layout";
import { ProtectedRoute } from "@/components/protected-route";
import { OverviewPage } from "@/pages/overview";
import { AccountPairsPage } from "@/pages/AccountPairs";
import { AccountPairDetailPage } from "@/pages/AccountPairDetail";
import { SymbolPairDetailPage } from "@/pages/SymbolPairDetail";
import { TerminalPage } from "@/pages/terminal";
import {
  FuturesAccountsPage,
  FuturesBotsPage,
  FuturesPartnerSetsPage,
  FuturesPlatformsPage,
  FuturesRiskPage,
} from "@/pages/futures";
import { PairProposalsPage } from "@/pages/PairProposals";
import { AccountsPage } from "@/pages/Accounts";
import { AccountDetailPage } from "@/pages/AccountDetail";
import { BotsPage } from "@/pages/Bots";
import { DecisionsPage } from "@/pages/decisions";
import { CompoundPage } from "@/pages/compound";
import { FirmsPage } from "@/pages/firms";
import { AlertsPage } from "@/pages/alerts";
import { SettingsPage } from "@/pages/settings";
import { ShareCardPage } from "@/pages/share-card";
import { AccountsMatcherPage } from "@/pages/accounts-matcher";
import { DialogImperativeProvider } from "@/components/ui/dialog-imperative";
import type { ReactNode } from "react";

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <DialogImperativeProvider>
        <Routes>
        {/* All dashboard routes are gated by ProtectedRoute. Login itself
            lives on pfh.trading — no auth UI in the dashboard. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            {/* / → Overview (operator landing)
                3-level hedge hierarchy:
                 /pairs                  level 1 — list of account pairs
                 /pairs/:slug            level 2 — symbol hedges under this account pair
                 /pairs/:slug/:symbol    level 3 — AI decisions + trade log for one symbol */}
            <Route index element={<OverviewPage />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/pairs" element={<AccountPairsPage />} />
            <Route path="/pairs/:slug" element={<AccountPairDetailPage />} />
            <Route path="/pairs/:slug/:symbol" element={<SymbolPairDetailPage />} />
            <Route path="/terminal" element={<TerminalPage market="fx-cfd" />} />
            <Route path="/terminal/:slug" element={<TerminalPage market="fx-cfd" />} />
            <Route path="/futures" element={<Navigate to="/futures/accounts" replace />} />
            <Route path="/futures/accounts" element={<FuturesAccountsPage />} />
            <Route path="/futures/partner-sets" element={<FuturesPartnerSetsPage />} />
            <Route path="/futures/terminal" element={<TerminalPage market="futures" />} />
            <Route path="/futures/terminal/:slug" element={<TerminalPage market="futures" />} />
            <Route path="/futures/platforms" element={<FuturesPlatformsPage />} />
            <Route path="/futures/risk" element={<FuturesRiskPage />} />
            <Route path="/futures/bots" element={<FuturesBotsPage />} />
            <Route path="/futures-terminal" element={<TerminalPage market="futures" />} />
            <Route path="/futures-terminal/:slug" element={<TerminalPage market="futures" />} />
            <Route path="/pair-approvals" element={<PairProposalsPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/accounts/:id" element={<AccountDetailPage />} />
            <Route path="/bots" element={<BotsPage />} />
            <Route path="/decisions" element={<DecisionsPage />} />
            <Route path="/compound" element={<CompoundPage />} />
            <Route path="/share-card" element={<ShareCardPage />} />
            <Route path="/firms" element={<FirmsPage />} />
            <Route path="/accounts-matcher" element={<AccountsMatcherPage />} />
            <Route path="/account-matcher" element={<Navigate to="/accounts-matcher" replace />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </DialogImperativeProvider>
    </BrowserRouter>
  );
}
